import { useEffect, useState } from "react";

import { sendVerifyEmailEmail } from "src/utils/snug/snugAuthApi";
import { SignupStepper } from "src/sections/starter-pay/signup-stepper";
import {
  readCustomPlanParams,
  customPlanQueryString,
} from "src/sections/starter-pay/custom-plan-storage";
import { readStarterPayContext } from "src/sections/starter-pay/context";

const SENT_FLAG_PREFIX = "sp_verify_sent:";

function hasAlreadySent(email: string): boolean {
  try {
    return localStorage.getItem(SENT_FLAG_PREFIX + email) === "1";
  } catch {
    return false;
  }
}

function markSent(email: string): void {
  try {
    localStorage.setItem(SENT_FLAG_PREFIX + email, "1");
  } catch {
    // ignore storage errors
  }
}

function buildReturnTo(): string {
  return "/get-started" + customPlanQueryString(readCustomPlanParams());
}

interface VerifyStepProps {
  email: string;
}

export function VerifyStep({ email: emailProp }: VerifyStepProps) {
  const email = emailProp || readStarterPayContext()?.email || "";
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!email) return;
    if (hasAlreadySent(email)) {
      setSent(true);
      return;
    }
    setSending(true);
    sendVerifyEmailEmail(email, false, buildReturnTo())
      .then(() => {
        markSent(email);
        setSent(true);
      })
      .catch(() =>
        setError("Could not send verification email. Please try again.")
      )
      .finally(() => setSending(false));
  }, [email]);

  const handleResend = async () => {
    if (!email || resending) return;
    setResending(true);
    setError("");
    try {
      await sendVerifyEmailEmail(email, false, buildReturnTo());
      markSent(email);
      setSent(true);
    } catch {
      setError("Could not resend verification email. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <SignupStepper currentStep="account" />

      <div
        className="sp-card"
        style={{ textAlign: "center", padding: "2.5rem 2rem" }}
      >
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/inbox.png"
          alt=""
          style={{ maxWidth: 48, marginBottom: "1rem" }}
        />
        <h2 className="sp-section-title" style={{ marginBottom: "0.5rem" }}>
          Check your inbox
        </h2>
        <p className="sp-section-help">
          We sent a verification link to <strong>{email}</strong>.<br />
          Click the link in the email to continue to payment.
        </p>
        <p
          className="sp-section-help"
          style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}
        >
          Can't find it? Check your spam folder.
        </p>
        <button
          type="button"
          className="sp-cta"
          style={{
            background: "transparent",
            color: "#3045C9",
            border: "1.5px solid #3045C9",
            marginTop: "1.5rem",
          }}
          disabled={sending || resending}
          onClick={handleResend}
        >
          {(sending || resending) ? (
            <span className="sp-cta-spinner" aria-hidden="true" />
          ) : null}
          {sending || resending
            ? "Sending…"
            : sent
            ? "Resend verification email"
            : "Send verification email"}
        </button>
      </div>

      {error && <div className="sp-error">{error}</div>}

      <p className="sp-disclaimer">
        The link expires after 24 hours. If you have trouble, contact{" "}
        <a href="mailto:hello@getsnug.com">hello@getsnug.com</a>.
      </p>
    </>
  );
}
